'use client';

// Carga MÚLTIPLE de adjuntos de la pre-radicación por categoría: lista los
// archivos ya subidos (nombre + ver + quitar) y un botón «+» para agregar más
// (regla del cliente: cada grupo de PDFs permite varios). Cada subida es un
// POST /solicitudes/:id/adjuntos; la lista vive en el backend (AdjuntoSolicitud).
// Para MEDIO_MAGNETICO acepta cualquier tipo de archivo (img/pdf/video) con el
// tope configurado en el backend (2 GB práctico).

import { useRef, useState } from 'react';
import { FileUp, Loader2, Paperclip, Plus, X } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import { descargarDocumento } from '@/lib/documentos';
import {
  eliminarAdjunto,
  subirAdjunto,
  type AdjuntoSolicitud,
  type CategoriaAdjunto,
} from '@/lib/preRadicacion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ACCEPT_PDF = 'application/pdf,image/*,.pdf,.doc,.docx';

export default function CampoAdjuntos({
  label,
  categoria,
  solicitudId,
  adjuntos,
  onCambio,
  asegurarSolicitud,
  disabled,
}: {
  label: string;
  categoria: CategoriaAdjunto;
  /** Id de la solicitud si ya existe; si no, se usa asegurarSolicitud. */
  solicitudId?: string | null;
  /** Adjuntos ya subidos DE ESTA categoría (el padre filtra). */
  adjuntos: AdjuntoSolicitud[];
  /** El padre recarga la solicitud (detalle con adjuntos) tras subir/quitar. */
  onCambio: (solicitudId: string) => void;
  /** Crea la solicitud mínima si aún no existe y devuelve su id. */
  asegurarSolicitud?: () => Promise<string | null>;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // El selector de archivo solo se muestra al presionar «+» (o si no hay nada).
  const [agregando, setAgregando] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const esMagnetico = categoria === 'MEDIO_MAGNETICO';

  async function handleSubir() {
    if (!file) {
      avisoError('Seleccione un archivo primero.');
      return;
    }
    setSubiendo(true);
    try {
      const id = solicitudId ?? (await asegurarSolicitud?.()) ?? null;
      if (!id) return; // asegurarSolicitud ya avisó qué falta
      await subirAdjunto(id, file, categoria);
      avisoExito(`Archivo agregado a «${label}».`);
      setFile(null);
      setAgregando(false);
      if (inputRef.current) inputRef.current.value = '';
      onCambio(id);
    } catch (err) {
      avisoError('No se pudo subir el archivo', err instanceof Error ? err.message : undefined);
    } finally {
      setSubiendo(false);
    }
  }

  async function quitar(a: AdjuntoSolicitud) {
    if (!solicitudId) return;
    try {
      await eliminarAdjunto(solicitudId, a.id);
      onCambio(solicitudId);
    } catch (err) {
      avisoError('No se pudo quitar el archivo', err instanceof Error ? err.message : undefined);
    }
  }

  const mostrarSelector = agregando || adjuntos.length === 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        {/* Botón «+» para cargar varios (pedido del cliente). */}
        {!disabled && !mostrarSelector && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 rounded-full px-2 text-xs"
            onClick={() => setAgregando(true)}
            aria-label={`Agregar otro archivo a ${label}`}
          >
            <Plus className="size-3.5" /> Agregar
          </Button>
        )}
      </div>

      {/* Archivos ya subidos de esta categoría. */}
      {adjuntos.length > 0 && (
        <ul className="space-y-1.5">
          {adjuntos.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-md border border-input bg-accent/40 px-3 py-2 text-sm"
            >
              <Paperclip className="size-4 shrink-0 text-primary" />
              <span className="min-w-0 flex-1 truncate">{a.nombre}</span>
              <button
                type="button"
                className="shrink-0 font-medium text-primary underline underline-offset-2"
                onClick={() => descargarDocumento(a.storage_key, a.nombre)}
              >
                ver
              </button>
              {!disabled && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-7 shrink-0 p-0"
                  onClick={() => quitar(a)}
                  aria-label="Quitar archivo"
                >
                  <X className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Selector + botón Subir (primera vez o tras presionar «+»). */}
      {!disabled && mostrarSelector && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Input
            ref={inputRef}
            type="file"
            accept={esMagnetico ? undefined : ACCEPT_PDF}
            disabled={subiendo}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="cursor-pointer"
          />
          <div className="flex shrink-0 gap-2">
            <Button type="button" onClick={handleSubir} disabled={subiendo || !file}>
              {subiendo ? (
                <>
                  <Loader2 className="size-4 animate-spin" /> Subiendo…
                </>
              ) : (
                <>
                  <FileUp className="size-4" /> Subir
                </>
              )}
            </Button>
            {adjuntos.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                disabled={subiendo}
                onClick={() => { setAgregando(false); setFile(null); }}
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>
      )}
      {esMagnetico && (
        <p className="text-xs text-muted-foreground">
          Acepta cualquier tipo de archivo (imagen, PDF, video…). La subida de archivos
          grandes puede tardar varios minutos.
        </p>
      )}
    </div>
  );
}
